import { useState, useEffect } from 'react';
import {
  Button,
  Badge,
  Input,
  Label,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@rnd-payplus/alloy-react';
import { Download, UserPlus, Plus, Pencil, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentTenantId } from '../utils/auth';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  lastLogin: string;
}

interface AuditLog {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  resource: string;
}

const SecurityPage = () => {
  const tenantId = getCurrentTenantId('default');

  const [users, setUsers] = useState<User[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);

  // Assign Role dialog
  const [assignRoleDialog, setAssignRoleDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  // Invite User dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'viewer' });
  const [tempPassword, setTempPassword] = useState('');
  const [inviteError, setInviteError] = useState('');

  // Assign Role dialog errors
  const [roleError, setRoleError] = useState('');

  // Users loading state
  const [usersLoading, setUsersLoading] = useState(true);

  // ─── Data loaders ─────────────────────────────────────────────────────────

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await fetch(`/api/users?tenantId=${tenantId}`);
      if (res.ok) {
        const data = await res.json();
        setUsers(
          data.map((u: any) => ({
            id: u.id,
            email: u.email,
            name: u.name || '',
            role: u.role,
            status: u.is_active ? 'active' : 'inactive',
            lastLogin: u.last_login_at
              ? new Date(u.last_login_at).toLocaleString()
              : 'Never',
          }))
        );
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await fetch('/api/audit-logs');
      if (!response.ok) {
        throw new Error(`Failed to load audit logs (${response.status})`);
      }
      const data = await response.json();
      const mapped = data.map((log: any) => ({
        id: String(log.id),
        timestamp: new Date(log.timestamp).toLocaleString(),
        user: log.user_id || 'System',
        action: log.action,
        resource: (log.resource_type || '') + (log.resource_id ? `: ${log.resource_id}` : ''),
      }));
      setAuditLogs(mapped);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setAuditLogs([]);
    }
  };

  useEffect(() => {
    loadUsers();
    loadAuditLogs();
  }, []);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleInviteUser = async () => {
    setInviteError('');
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...inviteForm, tenantId }),
      });
      const data = await res.json();
      if (res.ok) {
        setTempPassword(data.tempPassword);
        loadUsers();
      } else {
        setInviteError(data.error || 'Failed to invite user');
      }
    } catch (err) {
      console.error('Failed to invite user:', err);
      setInviteError('Failed to invite user');
    }
  };

  const handleAssignRole = async () => {
    setRoleError('');
    try {
      const res = await fetch(`/api/users/${selectedUser}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: selectedRole }),
      });
      if (res.ok) {
        setAssignRoleDialog(false);
        loadUsers();
      } else {
        const data = await res.json();
        setRoleError(data.error || 'Failed to assign role');
      }
    } catch (err) {
      console.error('Failed to assign role:', err);
      setRoleError('Failed to assign role');
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    try {
      const res = await fetch(`/api/users/${userId}`, { method: 'DELETE' });
      if (res.ok) {
        loadUsers();
      }
    } catch (err) {
      console.error('Failed to deactivate user:', err);
    }
  };

  const handleGenerateComplianceReport = () => {
    toast.info('Generating compliance report (SOC2/GDPR)...');
  };

  const handleCloseInvite = () => {
    setInviteOpen(false);
    setTempPassword('');
    setInviteForm({ email: '', name: '', role: 'viewer' });
    setInviteError('');
  };

  // ─── KPI Computations ──────────────────────────────────────────────────────
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.status === 'active').length;
  const totalAuditEntries = auditLogs.length;
  const uniqueActions = new Set(auditLogs.map(l => l.action)).size;

  // ─── Render ───────────────────────────────────────────────────────────────

  usePageShell({
    title: 'Security Center',
    secondaryActions: (
      <Button variant="outline" onClick={handleGenerateComplianceReport}>
        <Download className="mr-2 h-4 w-4" />
        Generate Compliance Report
      </Button>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Security' },
    ],
  });

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total Users" value={totalUsers} />
        <KPICard title="Active Users" value={activeUsers} />
        <KPICard title="Audit Entries" value={totalAuditEntries} subtitle="Total" />
        <KPICard title="Action Types" value={uniqueActions} subtitle="Unique" />
      </div>

      <Tabs defaultValue="users">
        <TabsList className="w-full mb-6">
          <TabsTrigger className="flex-1" value="users">User Roles</TabsTrigger>
          <TabsTrigger className="flex-1" value="audit">Audit Logs</TabsTrigger>
        </TabsList>

        {/* ── User Roles Tab ─────────────────────────────────────────────────── */}
        <TabsContent value="users">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">User Role Management</h2>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setInviteOpen(true)}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite User
                  </Button>
                  <Button onClick={() => setAssignRoleDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Assign Role
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <Spinner className="mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No users found. Invite users to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name || '\u2014'}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{user.role}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.status === 'active' ? 'success' : 'secondary'}>
                            {user.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.lastLogin}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Edit role"
                              onClick={() => {
                                setSelectedUser(user.id);
                                setSelectedRole(user.role);
                                setAssignRoleDialog(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {user.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive ml-1"
                                onClick={() => handleDeactivateUser(user.id)}
                              >
                                Deactivate
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
        </TabsContent>

        {/* ── Audit Logs Tab ─────────────────────────────────────────────────── */}
        <TabsContent value="audit">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Audit Logs</h2>
                <Button
                  variant="outline"
                  onClick={() => window.open('/api/audit-logs/export', '_blank')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
              <div className="flex gap-4 mb-4">
                <Input
                  placeholder="Search logs..."
                  className="flex-1"
                />
                <Select defaultValue="all">
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="create">Create</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.timestamp}</TableCell>
                      <TableCell>{log.user}</TableCell>
                      <TableCell>{log.action}</TableCell>
                      <TableCell>{log.resource}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
        </TabsContent>
      </Tabs>

      {/* ── Invite User Dialog ─────────────────────────────────────────────── */}
      <Dialog open={inviteOpen} onOpenChange={(open) => { if (!open) handleCloseInvite(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>Add a new user to your organization.</DialogDescription>
          </DialogHeader>

          {tempPassword ? (
            <div className="space-y-4 py-4">
              <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="font-semibold">User invited successfully!</p>
                  <p>
                    Temporary password (shown once):{' '}
                    <strong className="font-mono">{tempPassword}</strong>
                  </p>
                  <p>Share this with the user securely. They will be prompted to change it on first login.</p>
                </div>
              </div>
              <Button className="w-full" onClick={handleCloseInvite}>
                Done
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="inviteName">Full Name</Label>
                  <Input
                    id="inviteName"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inviteEmail">Email</Label>
                  <Input
                    id="inviteEmail"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inviteRole">Role</Label>
                  <Select
                    value={inviteForm.role}
                    onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                  >
                    <SelectTrigger id="inviteRole">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="operator">Operator</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {inviteError && (
                  <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{inviteError}</span>
                  </div>
                )}
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button onClick={handleInviteUser} disabled={!inviteForm.email}>
                  Send Invite
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Assign Role Dialog ─────────────────────────────────────────────── */}
      <Dialog open={assignRoleDialog} onOpenChange={(open) => { if (!open) { setAssignRoleDialog(false); setRoleError(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Role</DialogTitle>
            <DialogDescription>Select a user and assign them a role.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="assignUser">User</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger id="assignUser">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="assignRole">Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger id="assignRole">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {roleError && (
              <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{roleError}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={() => setRoleError('')}>Cancel</Button>
            </DialogClose>
            <Button onClick={handleAssignRole} disabled={!selectedUser || !selectedRole}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityPage;
